import UndoIcon from '@mui/icons-material/ArrowBackRounded';
import RedoIcon from '@mui/icons-material/ArrowForwardRounded';
import { Box, SvgIcon } from '@mui/material';
import type { ButtonProps } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Analytics } from '@/shared/lib';
import { Button as SharedButton } from '@/shared/ui';
import { Direction } from '../../model/types';
import { useTimeMachine } from '../../model/useTimeMachine';

const Button = styled((props: ButtonProps) => {
  return <SharedButton variant="text" color="secondary" {...props} />;
})(({ theme }) => ({
  width: 40,
  minWidth: 40,
  padding: theme.spacing(0.875),
  color: '#7E828F',
  border: '1px solid #BBBEC8',
  borderRadius: theme.shape.borderRadius * 2,

  '&:not(:first-of-type)': {
    marginLeft: -1,
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
  },

  '&:not(:last-of-type)': {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
  },

  '&:hover': {
    color: '#666666',
    backgroundColor: '#6666660a',
    borderColor: '#666666',
    zIndex: 1,
  },
}));

interface Props {
  snapshot: unknown;
  onReset: () => void;
  onTravel: (snapshot: unknown) => void;
}

export function TimeMachine({ snapshot, onReset, onTravel }: Props) {
  const { hasNext, hasPrevious, travel } = useTimeMachine({
    snapshot,
    onTravel,
  });

  const handleUndo = () => {
    travel(Direction.previous);
    Analytics.sendFeatureUsage('time_machine', 'undo');
  };
  const handleRedo = () => {
    travel(Direction.next);
    Analytics.sendFeatureUsage('time_machine', 'redo');
  };
  const handleReset = () => {
    onReset();
    Analytics.sendFeatureUsage('time_machine', 'reset');
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <Button
        title="Undo latest preset change"
        disabled={!hasPrevious}
        onClick={handleUndo}
      >
        <UndoIcon />
      </Button>
      <Button
        title="Redo latest preset change"
        disabled={!hasNext}
        onClick={handleRedo}
      >
        <RedoIcon />
      </Button>
      <Button title="Reset to vendor default preset" onClick={handleReset}>
        <SvgIcon>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3 19V5M13 6L7 12M7 12L13 18M7 12H21"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </SvgIcon>
      </Button>
    </Box>
  );
}
